/**
 * Global, outside-React feedback channel.
 *
 * Every feature owns its own page-local transient-feedback hook (`useFeedback`,
 * `useToast`, ...) paired with a locally-rendered `<Snackbar>` — see
 * `store-settings/lib/useFeedback.ts` for the canonical shape. Those are the right
 * tool whenever a component itself triggers the feedback (e.g. a save button's
 * `onSuccess`). This module covers the one case they can't: a notification that
 * must reach the user regardless of which screen happens to be mounted, raised
 * from code that isn't a React component at all — e.g. `apiClient`'s response
 * interceptor reacting to a `TENANT_ACCESS_DENIED` response. It mirrors the
 * `apiClient.ts` "register a handler, call it from anywhere" idiom (see
 * `setTenantAccessDeniedHandler`) instead of a page-local `useState`.
 *
 * `GlobalFeedbackSnackbar` (mounted once by `AppLayout`) is the sole subscriber.
 */

export type GlobalFeedbackSeverity = 'success' | 'error' | 'info' | 'warning';

export interface GlobalFeedbackMessage {
  severity: GlobalFeedbackSeverity;
  message: string;
}

type GlobalFeedbackListener = (feedback: GlobalFeedbackMessage) => void;

let listener: GlobalFeedbackListener | null = null;

/** Registers the single active listener (the mounted `GlobalFeedbackSnackbar`). `null` unregisters. */
export function setGlobalFeedbackListener(next: GlobalFeedbackListener | null): void {
  listener = next;
}

/**
 * Publish a transient global notification. No-ops (rather than throwing/queueing)
 * if nothing is currently mounted to show it — the same "best effort" contract
 * `publishGlobalFeedback` callers already accept from page-local toasts.
 */
export function publishGlobalFeedback(feedback: GlobalFeedbackMessage): void {
  listener?.(feedback);
}
